import * as React from "react";
import {
	Autocomplete,
	Box,
	Checkbox,
	Chip,
	FormControl,
	FormControlLabel,
	Grid,
	InputLabel,
	OutlinedInput,
	TextField,
	Typography,
} from "@mui/material";
import { Controller } from "react-hook-form";

import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";
import { DynamicField } from "@/components/dynamicField/dynamic-field";
import { DynamicFieldType } from "@/components/dynamicField/types";

const LANGUAGE_SUGGESTIONS = [
	"English",
	"Malay",
	"Mandarin",
	"Cantonese",
	"Tamil",
	"Thai",
	"Indonesian",
	"Vietnamese",
	"Tagalog",
	"Hindi",
];

export function BasicInformation({
	control,
	languages,
	onLanguagesChange,
	codeSwitch,
	onCodeSwitchChange,
}: {
	control: any;
	languages: string[];
	onLanguagesChange: (languages: string[]) => void;
	codeSwitch: boolean;
	onCodeSwitchChange: (value: boolean) => void;
}): React.JSX.Element {
	return (
		<Grid container spacing={2}>
			<Grid size={{ md: 12, xs: 12 }}>
				<Controller
					control={control}
					name="name"
					rules={{
						required: "Name is required",
						minLength: { value: 2, message: "Name must be at least 2 characters" },
					}}
					render={({ field, fieldState }) => (
						<FormControl error={Boolean(fieldState.error)} fullWidth required>
							<InputLabel>Name</InputLabel>
							<OutlinedInput {...field} placeholder="e.g. Contractor on speakerphone in a van" />
							{fieldState.error && <CustomFormHelperError text={fieldState.error.message || ""} />}
						</FormControl>
					)}
				/>
			</Grid>

			<Grid size={{ md: 12, xs: 12 }}>
				<Controller
					control={control}
					name="description"
					render={({ field }) => (
						<FormControl fullWidth>
							<InputLabel>Description</InputLabel>
							<OutlinedInput {...field} placeholder="A short note on when to reach for this caller" />
						</FormControl>
					)}
				/>
			</Grid>

			<Grid size={{ md: 12, xs: 12 }}>
				<DynamicField
					control={control}
					type={DynamicFieldType.TEXT_AREA}
					name="prompt"
					label="Who this caller is"
					dynamicField={{
						name: "prompt",
						required: true,
						type: DynamicFieldType.TEXT_AREA,
						label: "Who this caller is",
						// Describes who, not why they called. What they want is supplied per run, so one
						// persona can be pointed at many situations without being rewritten.
						// Asks for behaviour rather than a label, because a label ("hard of hearing")
						// gets played as a caricature that announces itself and tests nothing.
						placeholder:
							"Age, work, where they are, what makes them hard work on a call. Write behaviour, not labels — “misses a word in four and answers the question they thought they heard”, not “hard of hearing”. No sample dialogue, and not what they want from the call; that is set per run.",
						multiline: true,
					}}
				/>
			</Grid>

			<Grid size={{ md: 12, xs: 12 }}>
				<Autocomplete
					multiple
					freeSolo
					options={LANGUAGE_SUGGESTIONS}
					value={languages}
					onChange={(_event, value) => onLanguagesChange(value as string[])}
					renderTags={(value, getTagProps) =>
						value.map((option, index) => {
							const { key, ...rest } = getTagProps({ index });
							return <Chip key={key} size="small" label={option} {...rest} />;
						})
					}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Languages"
							placeholder="Add a language"
							helperText="Leave empty to let the caller follow the agent. Naming one holds them to it for the whole call."
						/>
					)}
				/>
			</Grid>

			{languages.length > 1 ? (
				<Grid size={{ md: 12, xs: 12 }}>
					{/* Only meaningful with more than one language, so it appears only then. */}
					<FormControl fullWidth>
						<FormControlLabel
							control={<Checkbox checked={codeSwitch} onChange={(event) => onCodeSwitchChange(event.target.checked)} />}
							label={
								<Box>
									<Typography variant="body2">Code-switches mid-sentence</Typography>
									<Typography variant="caption" color="text.secondary">
										Mixes languages the way bilingual speakers actually do, rather than picking one and staying with it.
										This is the case that breaks transcription.
									</Typography>
								</Box>
							}
							sx={{ alignItems: "flex-start", "& .MuiCheckbox-root": { pt: 0.5 } }}
						/>
					</FormControl>
				</Grid>
			) : null}

			<Grid size={{ md: 12, xs: 12 }}>
				<DynamicField
					control={control}
					type={DynamicFieldType.TEXT}
					name="settings.firstPhrase"
					label="Opening line"
					dynamicField={{
						name: "settings.firstPhrase",
						type: DynamicFieldType.TEXT,
						label: "Opening line",
						placeholder: "e.g. Hello?",
						description:
							"What they say on picking up, before they know who is calling. A greeting and nothing else — their mood, their hurry and their hearing come out later, through how they behave. Leave empty and the agent speaks first.",
					}}
				/>
			</Grid>

			<Grid size={{ md: 12, xs: 12 }}>
				<DynamicField
					control={control}
					type={DynamicFieldType.NUMBER}
					name="settings.maxTurns"
					label="Turn limit"
					dynamicField={{
						name: "settings.maxTurns",
						type: DynamicFieldType.NUMBER,
						label: "Turn limit",
						placeholder: "40",
						description: "Stops a caller and an agent talking past each other indefinitely.",
					}}
				/>
			</Grid>
		</Grid>
	);
}
